import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { decryptData } from "../../../utils/valueDecrypt";
import { Flex, Spin, Alert, Modal } from "antd";

function NewVideoConsult(props) {
  const { selectedPatient, fetchUrl, videoUrlExt } = props;

  const [url, setUrl] = useState("");
  const [mediaState, setMediaState] = useState("checking");
  const [mediaError, setMediaError] = useState("");
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  const permissionStreamRef = useRef(null);
  const mediaStateRef = useRef(mediaState);
  mediaStateRef.current = mediaState;

  const fetchedApptIdRef = useRef(null);
  const isFetchingLinkRef = useRef(false);

  const getAppointmentId = useCallback(() => {
    if (selectedPatient?.appointmentId) return selectedPatient.appointmentId;
    try {
      const raw = sessionStorage.getItem("selectedAppt");
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed?.appointmentId || null;
      }
    } catch (e) {
      console.error("Failed to parse selectedAppt from sessionStorage", e);
    }
    return null;
  }, [selectedPatient?.appointmentId]);

  const isMediaSupported = useCallback(() => {
    return (
      typeof window !== "undefined" &&
      window.isSecureContext &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function"
    );
  }, []);

  const stopPermissionStream = useCallback(() => {
    if (!permissionStreamRef.current) return;

    permissionStreamRef.current.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (e) {
        console.warn("Failed to stop media track:", e);
      }
    });

    permissionStreamRef.current = null;
  }, []);

  const getPermissionState = useCallback(async () => {
    let camera = "unknown";
    let microphone = "unknown";

    // 1. Try permissions query API
    if (navigator.permissions?.query) {
      try {
        const result = await navigator.permissions.query({
          name: "camera",
        });
        camera = result.state;
      } catch (e) {
        console.debug("Camera permission query unsupported:", e);
      }

      try {
        const result = await navigator.permissions.query({
          name: "microphone",
        });
        microphone = result.state;
      } catch (e) {
        console.debug("Microphone permission query unsupported:", e);
      }
    }

    // 2. Fallback: check enumerateDevices device labels
    // When permissions are already granted, device labels are non-empty strings
    if (navigator.mediaDevices?.enumerateDevices) {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCameraLabel = devices.some(
          (d) => d.kind === "videoinput" && d.label !== ""
        );
        const hasMicLabel = devices.some(
          (d) => d.kind === "audioinput" && d.label !== ""
        );

        if (hasCameraLabel) camera = "granted";
        if (hasMicLabel) microphone = "granted";
      } catch (e) {
        console.debug("Enumerate devices error:", e);
      }
    }

    return { camera, microphone };
  }, []);

  const requestMediaPermissions = useCallback(async () => {
    setMediaError("");

    if (!isMediaSupported()) {
      setMediaState("unsupported");
      setMediaError(
        "This browser does not support camera and microphone access, " +
          "or the page is not running over HTTPS."
      );
      return false;
    }

    try {
      setMediaState("requesting");

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
        },
        audio: true,
      });

      permissionStreamRef.current = stream;
      stopPermissionStream();

      setMediaState("granted");
      setMediaError("");

      return true;
    } catch (error) {
      console.error("Camera/microphone permission error:", error);

      stopPermissionStream();

      const errorName = error?.name;

      if (
        errorName === "NotAllowedError" ||
        errorName === "PermissionDeniedError"
      ) {
        setMediaState("denied");
        setMediaError(
          "Camera or microphone access is blocked for this site. " +
            "Allow Camera and Microphone in your browser site settings, " +
            "then press Retry."
        );
        setShowPermissionModal(true);
        return false;
      }

      if (errorName === "NotFoundError") {
        setMediaState("not-found");
        setMediaError("No camera or microphone was found on this device.");
        return false;
      }

      if (errorName === "NotReadableError") {
        setMediaState("busy");
        setMediaError(
          "The camera or microphone is already being used by another app or tab."
        );
        return false;
      }

      if (errorName === "SecurityError") {
        setMediaState("blocked");
        setMediaError(
          "The browser blocked access to your camera or microphone."
        );
        return false;
      }

      setMediaState("error");
      setMediaError(
        `Unable to access camera/microphone${
          error?.message ? `: ${error.message}` : "."
        }`
      );

      return false;
    }
  }, [isMediaSupported, stopPermissionStream]);

  const checkMediaPermissions = useCallback(async () => {
    if (!isMediaSupported()) {
      setMediaState("unsupported");
      return;
    }

    const permissions = await getPermissionState();
    console.log("Media permission state:", permissions);

    if (
      permissions.camera === "granted" &&
      permissions.microphone === "granted"
    ) {
      setMediaState("granted");
      return;
    }

    if (
      permissions.camera === "denied" ||
      permissions.microphone === "denied"
    ) {
      setMediaState("denied");
      setMediaError(
        "Camera and microphone are blocked for this site. " +
          "Allow them in browser site settings."
      );
      return;
    }

    // If already granted, do not downgrade back to prompt
    if (mediaStateRef.current === "granted") {
      return;
    }

    setMediaState("prompt");
  }, [getPermissionState, isMediaSupported]);

  useEffect(() => {
    checkMediaPermissions();
  }, [checkMediaPermissions]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      // Only re-check permissions if not already granted
      if (mediaStateRef.current !== "granted") {
        await checkMediaPermissions();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleVisibilityChange);
    };
  }, [checkMediaPermissions]);

  useEffect(() => {
    return () => {
      stopPermissionStream();
    };
  }, [stopPermissionStream]);

  const getLink = useCallback(async (apptId) => {
    const targetAppointmentId = apptId || getAppointmentId();
    if (!targetAppointmentId) return;
    if (isFetchingLinkRef.current) return;

    isFetchingLinkRef.current = true;
    setIframeError(false);

    let loginData = sessionStorage.getItem("login_result");
    let loginDataObject = loginData ? JSON.parse(loginData) : null;

    const formattedDate = new Date()
      .toISOString()
      .slice(0, 19)
      .replace("T", " ");

    const payload = {
      createdDate: formattedDate,
      merchantId: "hslA/69EPSD7qXTYAqWxuCqnq8KLo8Q3AFgvFVKsIBk=",
      merchantUserId: "HELIXO",
      modifiedDate: formattedDate,
      saltkey: "ojKdN1zB/MdTtJ1hBoIzVtRK60oyos5x29koJaadxL8=",
      uniqueId: targetAppointmentId,
      userName: loginDataObject?.user?.firstName || "Doctor",
    };

    try {
      const response = await axios.post(
        "https://api.v-connect.helixotech.com/HelixoConnect/join-call",
        payload
      );
      const decryptedString = decryptData(response.data.data);

      const decryptedJson =
        typeof decryptedString === "string"
          ? JSON.parse(decryptedString)
          : decryptedString;

      if (decryptedJson?.url) {
        setUrl(decryptedJson.url);
        fetchedApptIdRef.current = targetAppointmentId;

        if (fetchUrl) {
          fetchUrl(decryptedJson.url);
        }
      } else {
        throw new Error("Video call URL was not returned");
      }
    } catch (error) {
      console.error("Video link error:", error);
      setIframeError(true);
      setMediaError("Unable to create the video call. Please try again.");

      if (error.response) {
        console.log("Status:", error.response.status);
        console.log("Response:", error.response.data);
      } else if (error.request) {
        console.log("No response received");
      } else {
        console.log("Request error:", error.message);
      }
    } finally {
      isFetchingLinkRef.current = false;
    }
  }, [getAppointmentId, fetchUrl]);

  const handleEnableMedia = async () => {
    const granted = await requestMediaPermissions();
    if (granted) {
      setShowPermissionModal(false);
      setMediaState("granted");
      const apptId = getAppointmentId();
      if (apptId) {
        getLink(apptId);
      }
    }
  };

  useEffect(() => {
    if (mediaState !== "granted") return;
    const apptId = getAppointmentId();
    if (!apptId) return;
    if (fetchedApptIdRef.current === apptId && url) return;

    getLink(apptId);
  }, [mediaState, getAppointmentId, url, getLink]);

  const shouldShowPermissionGate = mediaState !== "granted";

  return (
    <div className="">
      <div className="relative">
        {shouldShowPermissionGate && (
          <div className="w-full min-h-[280px] flex items-center justify-center p-4">
            <div className="max-w-md w-full">
              {mediaState === "checking" && (
                <Flex align="center" justify="center" gap="middle">
                  <Spin size="large" tip="Checking camera and microphone..." />
                </Flex>
              )}

              {(mediaState === "prompt" || mediaState === "requesting") && (
                <div className="text-center">
                  <h2 className="text-lg font-semibold mb-2">
                    Camera & Microphone Access Required
                  </h2>

                  <p className="mb-4 text-sm text-gray-600">
                    Please allow your camera and microphone so you can join the
                    consultation.
                  </p>

                  <button
                    onClick={handleEnableMedia}
                    disabled={mediaState === "requesting"}
                    className="border-brand-primaryColor border bg-brand-primaryColor rounded-md px-4 py-2 text-white text-sm"
                  >
                    {mediaState === "requesting"
                      ? "Requesting Access..."
                      : "Enable Camera & Microphone"}
                  </button>
                </div>
              )}

              {(mediaState === "denied" || mediaState === "blocked") && (
                <div className="text-center">
                  <h2 className="text-lg font-semibold mb-2">
                    Camera & Microphone Blocked
                  </h2>

                  <p className="mb-3 text-sm text-gray-600">
                    Your browser has blocked camera or microphone access for
                    this site.
                  </p>

                  <Alert
                    type="warning"
                    showIcon
                    message="Allow camera and microphone in your browser/site settings."
                    description="After changing the permission, return to this page and press Retry."
                  />

                  <button
                    onClick={handleEnableMedia}
                    className="mt-4 border-brand-primaryColor border bg-brand-primaryColor rounded-md px-4 py-2 text-white text-sm"
                  >
                    Retry
                  </button>
                </div>
              )}

              {mediaState === "unsupported" && (
                <div className="text-center">
                  <h2 className="text-lg font-semibold mb-2">
                    Browser Not Supported
                  </h2>

                  <p className="text-sm text-gray-600">
                    Please open this consultation using a browser that supports
                    camera and microphone access and make sure the page is
                    served over HTTPS.
                  </p>
                </div>
              )}

              {mediaState === "not-found" && (
                <div className="text-center">
                  <h2 className="text-lg font-semibold mb-2">
                    Camera or Microphone Not Found
                  </h2>

                  <p className="text-sm text-gray-600">{mediaError}</p>
                </div>
              )}

              {mediaState === "busy" && (
                <div className="text-center">
                  <h2 className="text-lg font-semibold mb-2">
                    Camera or Microphone Busy
                  </h2>

                  <p className="text-sm text-gray-600">{mediaError}</p>

                  <button
                    onClick={handleEnableMedia}
                    className="mt-4 border-brand-primaryColor border bg-brand-primaryColor rounded-md px-4 py-2 text-white text-sm"
                  >
                    Retry
                  </button>
                </div>
              )}

              {mediaState === "error" && (
                <div className="text-center">
                  <h2 className="text-lg font-semibold mb-2">
                    Unable to Access Camera/Microphone
                  </h2>

                  <p className="mb-3 text-sm text-gray-600">{mediaError}</p>

                  <button
                    onClick={handleEnableMedia}
                    className="border-brand-primaryColor border bg-brand-primaryColor rounded-md px-4 py-2 text-white text-sm"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {mediaState === "granted" && (
          <>
            {!url && !iframeError && (
              <div className="w-full min-h-[280px] flex items-center justify-center">
                <Flex align="center" gap="middle">
                  <Spin size="large" tip="Preparing consultation..." />
                </Flex>
              </div>
            )}

            {iframeError && (
              <div className="w-full min-h-[280px] flex items-center justify-center p-4">
                <div className="text-center">
                  <h2 className="text-lg font-semibold mb-2">
                    Unable to Load Consultation
                  </h2>

                  <p className="mb-4 text-sm text-gray-600">
                    Please try again.
                  </p>

                  <button
                    onClick={() => {
                      setIframeError(false);
                      setUrl("");
                      fetchedApptIdRef.current = null;
                      const apptId = getAppointmentId();
                      if (apptId) {
                        getLink(apptId);
                      }
                    }}
                    className="border-brand-primaryColor border bg-brand-primaryColor rounded-md px-4 py-2 text-white text-sm"
                  >
                    Retry Call
                  </button>
                </div>
              </div>
            )}

            {url && !iframeError && (
              <>
                <div
                  id="videoload"
                  style={{
                    width: "100px",
                    height: "100px",
                    margin: "auto",
                    textAlign: "center",
                    display: "none",
                  }}
                >
                  Loading...
                </div>

                {!videoUrlExt ? (
                  <div id="videoframe" className="w-full min-h-[280px]">
                    <iframe
                      id="ccc"
                      scrolling="no"
                      src={url}
                      title="Video Consultation"
                      allow="camera; microphone; autoplay; fullscreen; display-capture;"
                      allowFullScreen
                      onError={(error) => {
                        console.error("Video iframe error:", error);
                        setIframeError(true);
                      }}
                      className="w-full h-[280px] border-0"
                    />
                  </div>
                ) : null}
              </>
            )}
          </>
        )}

        <Modal
          open={showPermissionModal}
          title="Camera & Microphone Permission Required"
          footer={
            <button
              onClick={handleEnableMedia}
              className="border-brand-primaryColor border bg-brand-primaryColor rounded-md px-4 py-2 text-white text-sm"
            >
              Retry
            </button>
          }
          onCancel={() => setShowPermissionModal(false)}
        >
          <p>Your browser has blocked access to the camera or microphone.</p>

          <p className="mt-3">
            Open the browser's site permissions, change Camera and Microphone
            from <strong>Block</strong> to <strong>Allow</strong>, then return
            here and press Retry.
          </p>
        </Modal>
      </div>
    </div>
  );
}

export default NewVideoConsult;
